import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import If from "../../../vendors/components/If";
import EventoModel from "../../shared/model/evento.model";
import InformeCard from "../informe-card";
import { filtrarInformesDoEvento } from "../../shared/@types/informeEvento";
import { useInformesEvento } from "../../shared/contexts/InformesEventoContext";
import "./index.scss";

interface Props {
  evento: EventoModel;
}

/**
 * RN007 - Ao acessar a página do evento, exibe automaticamente um único pop-up
 * com todos os comunicados/alertas vigentes daquele evento (abrangência E).
 * Possui botão de fechar (RN003) e um sino flutuante para reabrir.
 */
export default function EventoInformesPopup({ evento }: Props) {
  const { informes } = useInformesEvento();
  const codAudienciaPublica = evento?.codAudienciaPublica;

  const [visible, setVisible] = useState(false);
  const autoOpenRef = useRef(false);
  const eventoAtualRef = useRef<any>(null);

  const lista = useMemo(
    () => filtrarInformesDoEvento(informes, codAudienciaPublica),
    [informes, codAudienciaPublica]
  );

  // Reseta o controle de abertura automática quando o evento muda,
  // garantindo que o pop-up abra novamente a cada novo acesso.
  useEffect(() => {
    if (eventoAtualRef.current !== codAudienciaPublica) {
      eventoAtualRef.current = codAudienciaPublica;
      autoOpenRef.current = false;
    }
  }, [codAudienciaPublica]);

  useEffect(() => {
    if (!autoOpenRef.current && lista.length > 0) {
      autoOpenRef.current = true;
      setVisible(true);
    }
  }, [lista.length]);

  const titulo = useMemo(() => {
    const tipo = evento?.cdTpEventoNavigation?.dsTpEvento;
    const numero = evento?.nuAudienciaPublica;
    const ano = evento?.nuAno;
    if (numero) {
      const base = tipo ? `${tipo} nº ${numero}` : `Evento nº ${numero}`;
      return ano ? `${base}/${ano}` : base;
    }
    return "Comunicados e Alertas";
  }, [
    evento?.cdTpEventoNavigation?.dsTpEvento,
    evento?.nuAudienciaPublica,
    evento?.nuAno,
  ]);

  const situacao = evento?.codSituacaoAudienciaNavigation?.dsSituacaoAudiencia;

  const situacaoClasse = useMemo(() => {
    const s = (situacao || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    if (s.includes("encerr") || s.includes("cancel") || s.includes("conclu")) {
      return "evento-informes-dialog-situacao--encerrado";
    }
    if (s.includes("suspens")) {
      return "evento-informes-dialog-situacao--suspenso";
    }
    return "evento-informes-dialog-situacao--ativo";
  }, [situacao]);

  if (lista.length <= 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="evento-informes-sino"
        aria-label="Ver comunicados e alertas do evento"
        onClick={() => setVisible(true)}
      >
        <i className="pi pi-bell" />
        <span className="evento-informes-sino-badge">{lista.length}</span>
      </button>

      <Dialog
        visible={visible}
        draggable={false}
        className="evento-informes-dialog"
        style={{ width: lista.length <= 1 ? "520px" : "820px", maxWidth: "95vw" }}
        onHide={() => setVisible(false)}
        header={
          <div className="evento-informes-dialog-header">
            <i className="pi pi-volume-up" />
            <span className="evento-informes-dialog-titulo">{titulo}</span>
            <If condition={!!situacao}>
              <span
                className={`evento-informes-dialog-situacao ${situacaoClasse}`}
              >
                {situacao}
              </span>
            </If>
          </div>
        }
      >
        <div className="evento-informes-cards">
          {lista.map((informe) => (
            <InformeCard key={informe.cdSeqInformeEvento} informe={informe} />
          ))}
        </div>
      </Dialog>
    </>
  );
}
